import { publicApi, siteApi } from "@api";
import { QUERY_ALL } from "@constants";
import useUserMode from "@hooks/useUserMode";
import { useMemo } from "react";

/**
 * Hook to get game data source based on user mode
 * OPEN mode will use public API
 * MEMBER mode will use site API
 */
const useGameDataSource = () => {
  const { isGuest, isMember } = useUserMode();

  // MEMBER MODE
  const { data: memberProvidersResponse } = siteApi.useGetGameProviderListQuery(
    {
      query: QUERY_ALL,
    },
    {
      skip: isGuest,
    },
  );
  const { data: memberCategoriesResponse, isFetching: isFetchingMemberGameCategory } =
    siteApi.useGetGameCategoryListQuery(
      {
        query: QUERY_ALL,
      },
      {
        skip: isGuest,
      },
    );
  const { data: memberProvidersStatsResponse, isFetching: isFetchingMemberProviderStats } =
    siteApi.useGetGameProviderStatsQuery(undefined, {
      skip: isGuest,
    });
  const [getMemberGameList] = siteApi.useLazyGetGameListQuery();

  // OPEN MODE
  const { data: publicProvidersResponse } = publicApi.useGetPublicGameProviderListQuery(
    {
      query: QUERY_ALL,
    },
    {
      skip: isMember,
    },
  );
  const { data: publicCategoriesResponse, isFetching: isFetchingPublicGameCategory } =
    publicApi.useGetPublicGameCategoryListQuery(
      {
        query: QUERY_ALL,
      },
      {
        skip: isMember,
      },
    );
  const { data: publicProvidersStatsResponse, isFetching: isFetchingPublicProviderStats } =
    publicApi.useGetPublicGameProviderStatsQuery(undefined, {
      skip: isMember,
    });
  const [getPublicGameList] = publicApi.useLazyGetPublicGameListQuery();

  // combine the data
  const gameData = useMemo(() => {
    let providersResponse = null;
    let categoriesResponse = null;
    let providersStatsResponse = null;
    let isFetchingGameCategory = false;
    let isFetchingProviderStats = false;
    let getGameList = null;

    if (isMember) {
      providersResponse = memberProvidersResponse;
      categoriesResponse = memberCategoriesResponse;
      providersStatsResponse = memberProvidersStatsResponse;
      isFetchingGameCategory = isFetchingMemberGameCategory;
      isFetchingProviderStats = isFetchingMemberProviderStats;
      getGameList = getMemberGameList;
    }

    if (isGuest) {
      providersResponse = publicProvidersResponse;
      categoriesResponse = publicCategoriesResponse;
      providersStatsResponse = publicProvidersStatsResponse;
      isFetchingGameCategory = isFetchingPublicGameCategory;
      isFetchingProviderStats = isFetchingPublicProviderStats;
      getGameList = getPublicGameList;
    }

    return {
      providersResponse,
      categoriesResponse,
      providersStatsResponse,
      isFetchingGameCategory,
      isFetchingProviderStats,
      getGameList,
    };
  }, [
    getMemberGameList,
    getPublicGameList,
    isFetchingMemberGameCategory,
    isFetchingMemberProviderStats,
    isFetchingPublicGameCategory,
    isFetchingPublicProviderStats,
    memberCategoriesResponse,
    memberProvidersResponse,
    memberProvidersStatsResponse,
    publicCategoriesResponse,
    publicProvidersResponse,
    publicProvidersStatsResponse,
    isMember,
    isGuest,
  ]);

  return gameData;
};

export default useGameDataSource;
